// src/components/GlobalSearch.tsx
import React, { useState, useEffect, useRef } from 'react';
import {
  TextField,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Typography,
  Box,
  Chip,
  InputAdornment,
  IconButton,
  Divider
} from '@mui/material';
import {
  Search,
  Close,
  Folder,
  Event,
  Person,
  Inventory2,
  LocationOn,
  EventNote,
  Description,
  MusicNote,
  Receipt,
  Assessment
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from '../hooks/useDebounce';
import axiosInstance from '../utils/axiosConfig';

interface SearchResult {
  id: string;
  title: string;
  subtitle: string;
  type: 'resource' | 'performance' | 'personnel' | 'inventory' | 'venue' | 'event' | 'contract';
  url: string;
  date: string;
  status: string;
  metadata: Record<string, any>;
}

interface GlobalSearchProps {
  placeholder?: string;
  onResultClick?: () => void;
}

const GlobalSearch: React.FC<GlobalSearchProps> = ({ 
  placeholder = "Search everything...", 
  onResultClick 
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const debouncedQuery = useDebounce(query, 300);
  const navigate = useNavigate();
  const searchRef = useRef<HTMLDivElement>(null);

  // Type icons
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'resource': return <Folder fontSize="small" />;
      case 'performance': return <Event fontSize="small" />;
      case 'personnel': return <Person fontSize="small" />;
      case 'inventory': return <Inventory2 fontSize="small" />;
      case 'venue': return <LocationOn fontSize="small" />;
      case 'event': return <EventNote fontSize="small" />;
      case 'contract': return <Description fontSize="small" />;
      case 'band': return <MusicNote fontSize="small" />;
      case 'programming': return <Event fontSize="small" />;  
      case 'strand': return <Folder fontSize="small" />;
      case 'postproduction': return <Assessment fontSize="small" />;
      case 'purchaseorder': return <Receipt fontSize="small" />;
      default: return <Search fontSize="small" />;
    }
  };

  // Type colors
  const getTypeColor = (type: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (type) {
      case 'resource': return 'primary';
      case 'performance': return 'secondary';
      case 'personnel': return 'info';
      case 'inventory': return 'warning';
      case 'venue': return 'success';
      case 'event': return 'error';
      case 'contract': return 'default';
      case 'band': return 'secondary';
      case 'programming': return 'primary';  
      case 'strand': return 'info';
      case 'postproduction': return 'success';
      case 'purchaseorder': return 'error';
      default: return 'default';
    }
  };

  // Search function
  useEffect(() => {
    const performSearch = async () => {
      if (debouncedQuery.length < 2) {
        setResults([]);
        setShowResults(false);
        return;
      }

      setLoading(true);
      try {
        const response = await axiosInstance.get(`/api/search/global?q=${encodeURIComponent(debouncedQuery)}&limit=15`);
        setResults(response.data);
        setShowResults(true);
      } catch (error) {
        console.error('Search error:', error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    };

    performSearch();
  }, [debouncedQuery]);

  // Handle result click
  const handleResultClick = (result: SearchResult) => {
    navigate(result.url);
    setQuery('');
    setShowResults(false);
    onResultClick?.();
  };

  // Handle clear
  const handleClear = () => {
    setQuery('');
    setResults([]);
    setShowResults(false);
  };

  // Close results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Group results by type
  const groupedResults = results.reduce((acc, result) => {
    if (!acc[result.type]) {
      acc[result.type] = [];
    }
    acc[result.type].push(result);
    return acc;
  }, {} as Record<string, SearchResult[]>);

  const typeLabels = {
    resource: 'Resources',
    performance: 'Performances', 
    personnel: 'Personnel',
    inventory: 'Inventory',
    venue: 'Venues',
    event: 'Events',
    contract: 'Contracts',
    band: 'Bands',
    programming: 'Programming', 
    strand: 'Strands',
    postproduction: 'Post-Production',
    purchaseorder: 'Purchase Orders'
  };

  return (
    <Box ref={searchRef} sx={{ position: 'relative', width: '100%'}}>
      <TextField
        fullWidth
        size="small"
        placeholder={placeholder}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => query.length >= 2 && setShowResults(true)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: query && (
            <InputAdornment position="end">
              <IconButton size="small" onClick={handleClear}>
                <Close fontSize="small" />
              </IconButton>
            </InputAdornment>
          )
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'background.paper',
          }
        }}
      />

      {showResults && (query.length >= 2) && (
        <Paper
          elevation={8}
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1300,
            maxHeight: 400,
            overflow: 'auto',
            mt: 1
          }}
        >
          {loading ? (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Searching...
              </Typography>
            </Box>
          ) : results.length === 0 ? (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No results found for "{query}"
              </Typography>
            </Box>
          ) : (
            <List dense>
              {Object.entries(groupedResults).map(([type, typeResults], groupIndex) => (
                <React.Fragment key={type}>
                  {groupIndex > 0 && <Divider />}
                  
                  <ListItem sx={{ py: 0.5, backgroundColor: 'grey.50' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                      {typeLabels[type as keyof typeof typeLabels]} ({typeResults.length})
                    </Typography>
                  </ListItem>

                  {typeResults.map((result) => (
                    <ListItem
                      key={result.id}
                      button
                      onClick={() => handleResultClick(result)}
                      sx={{
                        '&:hover': {
                          backgroundColor: 'action.hover'
                        }
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {getTypeIcon(result.type)}
                      </ListItemIcon>
                      
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {result.title}
                            </Typography>
                            <Chip
                              label={result.type}
                              size="small"
                              color={getTypeColor(result.type)}
                              sx={{ height: 16, fontSize: '0.6rem' }}
                            />
                          </Box>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {result.subtitle}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </React.Fragment>
              ))}

              {results.length >= 15 && (
                <ListItem>
                  <ListItemText>
                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      Showing first 15 results. Try a more specific search.
                    </Typography>
                  </ListItemText>
                </ListItem>
              )}
            </List>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default GlobalSearch;